import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Grid2 as Grid, IconButton, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import { fetchArea, fetchStructure, resolveArea, type AreaResponse } from '../api/structure';
import { PageHeader } from '../components/PageHeader';
import { DataState } from '../components/DataState';
import { MetricChart } from '../components/MetricChart';
import { MetricsTable } from '../components/MetricsTable';

type PeriodMode = 'quarter' | 'year';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function monthsForPeriod(mode: PeriodMode, year: number, quarter: number): string[] {
  if (mode === 'year') {
    return Array.from({ length: 12 }, (_, i) => `${year}-${pad2(i + 1)}`);
  }
  const startMonth = (quarter - 1) * 3 + 1;
  return Array.from({ length: 3 }, (_, i) => `${year}-${pad2(startMonth + i)}`);
}

function periodLabel(mode: PeriodMode, year: number, quarter: number): string {
  return mode === 'year' ? String(year) : `Q${quarter} ${year}`;
}

export function AreaPage() {
  const { layerSlug = '', areaSlug = '' } = useParams();

  const now = new Date();
  const [mode, setMode] = useState<PeriodMode>('year');
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);

  const structureQuery = useQuery({
    queryKey: ['structure'],
    queryFn: ({ signal }) => fetchStructure(signal),
  });

  const resolved = resolveArea(structureQuery.data, layerSlug, areaSlug);

  const areaQuery = useQuery({
    queryKey: ['area', resolved?.layer ?? '', resolved?.area ?? ''],
    queryFn: ({ signal }) => fetchArea(resolved!.layer, resolved!.area, signal),
    enabled: !!resolved,
  });

  const title = resolved?.area ?? 'Area';
  const subtitle = resolved?.layer;

  const notFound = !structureQuery.isLoading && !structureQuery.error && !resolved;
  const loading = structureQuery.isLoading || (!!resolved && areaQuery.isLoading);
  const error = structureQuery.error ?? (resolved ? areaQuery.error : undefined);

  const hasData = !!areaQuery.data && areaQuery.data.metrics.length > 0;

  const targetMonths = monthsForPeriod(mode, year, quarter);
  const periodStart = targetMonths[0];
  const periodEnd = targetMonths[targetMonths.length - 1];

  const dataMonths = areaQuery.data?.months ?? [];
  const canGoPrev = dataMonths.some((m) => m < periodStart);
  const canGoNext = dataMonths.some((m) => m > periodEnd);

  function goPrev() {
    if (mode === 'year') {
      setYear((y) => y - 1);
    } else {
      if (quarter === 1) {
        setYear((y) => y - 1);
        setQuarter(4);
      } else {
        setQuarter((q) => q - 1);
      }
    }
  }

  function goNext() {
    if (mode === 'year') {
      setYear((y) => y + 1);
    } else {
      if (quarter === 4) {
        setYear((y) => y + 1);
        setQuarter(1);
      } else {
        setQuarter((q) => q + 1);
      }
    }
  }

  const periodNav = hasData ? (
    <Stack direction="row" alignItems="center" gap={1}>
      <IconButton size="small" onClick={goPrev} disabled={!canGoPrev} aria-label="Previous period">
        <ChevronLeftRounded />
      </IconButton>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(_, v) => { if (v) setMode(v); }}
        aria-label="Period mode"
      >
        <ToggleButton value="quarter">Quarter</ToggleButton>
        <ToggleButton value="year">Year</ToggleButton>
      </ToggleButtonGroup>
      <IconButton size="small" onClick={goNext} disabled={!canGoNext} aria-label="Next period">
        <ChevronRightRounded />
      </IconButton>
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
        {periodLabel(mode, year, quarter)}
      </Typography>
    </Stack>
  ) : undefined;

  return (
    <Box>
      <PageHeader title={title} subtitle={subtitle} actions={periodNav} />

      {notFound ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            This area could not be found. It may have been removed, or the dashboard data has not been synced yet.
          </Typography>
        </Box>
      ) : (
        <DataState
          loading={loading}
          error={error}
          data={areaQuery.data}
          isEmpty={(d: AreaResponse) => d.metrics.length === 0}
          emptyMessage="No metrics in this area yet."
        >
          {(data) => {
            const targetSet = new Set(targetMonths);
            const visibleMonths = data.months.filter((m) => targetSet.has(m));

            if (visibleMonths.length === 0) {
              return (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    No data for this period.
                  </Typography>
                </Box>
              );
            }

            const monthSet = new Set(visibleMonths);
            const visibleMetrics = data.metrics.map((m) => ({
              ...m,
              series: m.series.filter((p) => monthSet.has(p.month)),
            }));

            return (
              <Stack spacing={4}>
                <Grid container spacing={2}>
                  {visibleMetrics.map((m) => (
                    <Grid key={`${m.subarea}::${m.metric}`} size={{ xs: 12, lg: 6 }}>
                      <MetricChart metric={m} />
                    </Grid>
                  ))}
                </Grid>

                <Box>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    All data
                  </Typography>
                  <MetricsTable months={visibleMonths} metrics={visibleMetrics} />
                </Box>
              </Stack>
            );
          }}
        </DataState>
      )}
    </Box>
  );
}
